import React, { useMemo, useEffect } from "react";
import { useCommonStateFields } from "features/queryReports/hooks/useCommonStateFields";
import { applyCommonOutputs } from "features/queryReports/utils/queryCommon";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { readableRefDate } from "util/medicareRefDate.js";
import classes from "./Reports.module.scss";
import {
  disableSubmitButton,
  joinCounties,
  validateFileName
} from "features/queryReports/utils/commonFunctions";
import QueryCreatedModal from "features/queryReports/components/QueryCreatedModal";
import { useSubmitQuery } from "features/queryReports/hooks/useSubmitQuery";
import { countyLevelServicesIDs } from "features/queryReports/utils/reportIds";
import {
  CheckboxInput,
  CountiesSelect,
  FileNameInput,
  RefDateSelect,
  ReportTypeSelect
} from "features/queryReports/components/FormFields";
import countyLevelServicesOutputFields from "features/queryReports/utils/reportOutputFields/CountyLevelServicesOutputFields";

function QueryLogicDescription({ comboRefDate, queryObj }) {
  if (queryObj.parameters.fips.length > 0) {
    return (
      <span>
        I would like to see <strong>Medicare and Commercial</strong> patients
        located in the{" "}
        <strong>
          {queryObj.parameters.fips.length > 1 ? "counties" : "county"} of{" "}
          {joinCounties(queryObj.parameters.fips, true)}
        </strong>{" "}
        who received care from <strong>organizations</strong>{" "}
        <span>
          between <strong>{readableRefDate(comboRefDate)}</strong>
        </span>
        .
      </span>
    );
  } else {
    return null;
  }
}

function CountyLevelServices({ bridge, payerMixValue, initialParams, showOpenMyQueries }) {
  const {
    categoryId,
    msdrgReportId,
    cpthcpcsReportId,
    primaryDiagnosisReportId
  } = countyLevelServicesIDs;
  const {
    state: common,
    bind,
    set,
    setOutput,
    resetCommon,
    hydrateFrom,
    selectors
  } = useCommonStateFields({
    overrides: { payerMix: payerMixValue ?? "" }
  });

  const {
    submitQuery,
    waiting,
    showModal,
    modalData,
    closeModal
  } = useSubmitQuery({
    bridge,
    categoryId,
    classes,
    fileName: common.fileName
  });

  const confirmQuerySuccess = type => {
    if (type === 1) {
      closeModal();
      bridge.onMyQueryOpen();
    } else if (type === 2) {
      closeModal();
      bridge.onQuerySubmitted();
    }
  };

  const applyIncomingParams = data => {
    if (data.reportId === msdrgReportId) {
      set("reportType", "msdrg");
    } else if (data.reportId === cpthcpcsReportId) {
      set("reportType", "cpt/hcpcs");
    } else if (data.reportId === primaryDiagnosisReportId) {
      set("reportType", "primaryDiagnosis");
    }

    if (
      data.definition.outputFields.filter(x =>
        [
          "CPT Service Line",
          "ICD-10 Service Line",
          "MSDRG Service Line"
        ].includes(x)
      ).length > 0
    ) {
      set("codeServiceLine", true);
    }
  };

  useEffect(() => {
    if (initialParams) {
      hydrateFrom(initialParams);
      applyIncomingParams(initialParams); // local compute set at initial data
      // any page-only hydration here as needed
    }
  }, [initialParams]);

  const queryObj = useMemo(() => {
    const retObj = {
      description: common.fileName,
      parameters: {
        geography_type: "fipscode",
        quarter: common.comboRefDate,
        fips:
          common.countiesSelection.length > 0 ? common.countiesSelection : []
      },
      outputFields: [
        common.outputs.npi ? "Organization NPI" : null,
        common.outputs.name ? "Organization Name" : null,
        common.outputs.classification ? "Organization Classification" : null,
        common.outputs.specialization ? "Organization Specialization" : null
      ].filter((item) => item)
    };

    // common outputs
    applyCommonOutputs(retObj, common.outputs, {
      allowList: [
        "fipsCode",
        "fipsCountyName",
        "fipsState",
        "city",
        "stateName",
        "zipCode",
        "source",
        "distinctClaimsCount",
        "distinctPatientsCount",
        "proceduresCount",
        "sumCharges"
      ]
    });

    switch (common.reportType) {
      case "cpt/hcpcs":
        retObj.reportId = cpthcpcsReportId;
        if (common.outputs.codeServiceLine) {
          retObj.outputFields.push("CPT Service Line");
        }
        break;
      case "primaryDiagnosis":
        retObj.reportId = primaryDiagnosisReportId;
        if (common.outputs.codeServiceLine) {
          retObj.outputFields.push("ICD-10 Service Line");
        }
        break;
      case "msdrg":
        retObj.reportId = msdrgReportId;
        if (common.outputs.codeServiceLine) {
          retObj.outputFields.push("MSDRG Service Line");
        }
        break;
      default:
        console.log("reportType invalid");
    }

    return retObj;
  }, [
    common.fileName,
    common.comboRefDate,
    common.countiesSelection,
    common.reportType,
    common.outputs
  ]);

  function clearFields() {
    if (!showOpenMyQueries) {
      bridge?.onClickCancelQueryReport();
      return;
    }
    resetCommon(); // clears shared fields
    // clear page-only here
  }

  return (
    <div className="container-fluid p-3">
      <div className={classes["report-content-wrap"]}>
        <FileNameInput
          fileName={common.fileName}
          setFileName={v => set("fileName", v)}
          priorQueryParams={common.priorQueryParams}
        />
        <ReportTypeSelect
          bind={bind}
          label="Report Type:"
          overlayText="Choose report type to determine what codes are returned in your results"
          categoryId={categoryId}
        />

        <RefDateSelect
          id="comboRefDate"
          label="Commercial + Medicare Reference Date:"
          {...bind.select("comboRefDate")}
          placeholder="Select Commercial + Medicare Reference Date"
          overlayText="Commercial + Medicare reference date"
        />

        <CountiesSelect common={common} queryObj={queryObj} set={set} />

        {common.countiesSelection.length > 0 && (
          <div className="row mb-3">
            <div className={classes["querylogic-wrapper"]}>
              <FontAwesomeIcon
                icon={faCircleInfo}
                className={classes["info-icon"]}
              />
              <div>
                <div className={classes["header"]}>Query Logic</div>
                <div className={classes["body"]}>
                  <QueryLogicDescription
                    comboRefDate={common.comboRefDate}
                    queryObj={queryObj}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row mb-3">
          <div className={classes["querylogic-wrapper"]}>
            <FontAwesomeIcon
              icon={faCircleInfo}
              className={classes["info-icon"]}
            />
            <div>
              <div className={classes["header"]}>Output Fields</div>
                 <div className={classes["body"]}>
                  {countyLevelServicesOutputFields.map((item, index) => (
                    <CheckboxInput
                      key={item.name || index}
                      item={item}
                      common={common}
                      selectors={selectors}
                      setOutput={setOutput}
                    />
                  ))}
                </div>
            </div>
          </div>
        </div>

        {common.fileName &&
          common.reportType &&
          common.comboRefDate &&
          common.countiesSelection &&
          common.countiesSelection.length > 0 && (
            <div className={`row ${classes["submit-btn-wrap"]}`}>
              <button
                type="button"
                className={`btn btn-primary mr-2 ${classes["submit-btn"]}`}
                disabled={!validateFileName(common.fileName) || disableSubmitButton(common)}
                onClick={() => submitQuery(queryObj)}
              >
                Submit Query
                {waiting && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
              </button>
              <button
                type="button"
                className={`btn btn-secondary ${classes["cancel-btn"]}`}
                onClick={() => clearFields()}
              >
                Cancel
              </button>
            </div>
          )}

        <QueryCreatedModal
          show={showModal}
          header={modalData.header}
          body={modalData.body}
          classes={classes}
          onClose={() => confirmQuerySuccess(2)}
          onOpenMyQueries={() => confirmQuerySuccess(1)}
          showOpenMyQueries={showOpenMyQueries}
        />
      </div>
    </div>
  );
}

export default CountyLevelServices;
